import * as React from "react";
import { Box, Button, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export interface MobileErrorBannerProps {
  errorCount: number;
  /** Section id to jump to (first section containing an error). */
  jumpToSectionId?: string;
  onJump?: (sectionId: string) => void;
}

/**
 * Sticky red strip rendered above the MobileActionBar whenever the form has
 * validation errors.  Replaces the desktop `alert("Please fill out…")` popups
 * on mobile.
 *
 * Renders nothing when errorCount === 0.
 */
export function MobileErrorBanner({
  errorCount,
  jumpToSectionId,
  onJump,
}: MobileErrorBannerProps) {
  if (errorCount === 0) return null;

  const label =
    errorCount === 1
      ? "1 error needs attention"
      : `${errorCount} errors need attention`;

  return (
    <Box
      sx={{
        backgroundColor: "#fcebeb",
        borderTop: "1px solid #f7c1c1",
        px: 1.5,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
      role="alert"
    >
      <WarningAmberIcon sx={{ color: "#a32d2d", fontSize: 18 }} />
      <Typography variant="body2" sx={{ color: "#791f1f", flex: 1 }}>
        {label}
      </Typography>
      {jumpToSectionId && onJump && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onJump(jumpToSectionId)}
          sx={{
            color: "#791f1f",
            borderColor: "#f09595",
            "&:hover": { borderColor: "#a32d2d", backgroundColor: "#fadbdb" },
          }}
        >
          Review
        </Button>
      )}
    </Box>
  );
}
